var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
/**
 * Hook for record-based picker fields (authority lookup, vocabulary selection).
 * Returns options compatible with Stripes Selection component.
 *
 * CZNA-1033: the options are a window of the register (rejstřík) opened at the typed
 * text — `GET /records/index?pickable=true`, the order of the Rejstříky screen — not a
 * search of `GET /records`. See `utils/pickerRegister.ts` for why. A row is a heading;
 * picking it stores its record and the record's PREFERRED heading, which a variant row
 * names in its label ("… → vloží se …").
 *
 * Replaces useVocabularyOptions — works for ALL record types (VOCABULARY_*, GEOGRAPHIC, SUBJECT, etc.)
 *
 * CZNA-197: for authority picker targets (GEOGRAPHIC) the dropdown also includes
 * pending proposals (PENDING_REVIEW + CHANGES_REQUESTED) so a cataloger can pick
 * an authority that has been proposed but not yet approved. Proposals are not in the
 * register; they follow its rows and are visually flagged ("(návrh)"). Records win on a
 * heading collision (a proposal already published as a record is not shown twice).
 */
import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useOkapiKy } from '@folio/stripes/core';
import { useQuery } from 'react-query';
import { fetchRecordIndex } from '../api/recordService';
import { fetchProposals } from '../api/proposalService';
import { RecordType, ProposalStatus, isTwoStageApproval, ProposalType } from '../types/api';
import { PICKER_OPEN_WINDOW, PICKER_WINDOW, firstRowPerRecord, insertedHeading, registerRowLabel, typedAnchor, } from '../utils/pickerRegister';
var DEBOUNCE_MS = 200;
/** Pending proposals fetched per typed text (CZNA-197). */
var PROPOSAL_LIMIT = 50;
/**
 * CZNA-197: record types whose picker should also surface pending proposals.
 * GEOGRAPHIC only — its propose flow hands back a proposal. PERSONAL is a propose
 * target too (CZNA-321), but its child form publishes, so what it creates is already
 * a record and appears among the records; a pending PERSONAL proposal is not offered.
 */
var PROPOSAL_INCLUSIVE_TYPES = new Set([RecordType.GEOGRAPHIC]);
/**
 * Proposal statuses included in the picker. DRAFT is a private work-in-progress
 * state and APPROVED already exists as a record (would be a duplicate), so both
 * are excluded. The proposals endpoint requires a singular status, so each is
 * fetched in its own query.
 */
var PICKER_PROPOSAL_STATUSES = [
    ProposalStatus.PENDING_REVIEW,
    ProposalStatus.CHANGES_REQUESTED,
];
/**
 * CZNA-552 — a two-stage proposal that has passed local review is just as pickable as one
 * that has not, so PENDING_NATIONAL_REVIEW must be searched too; leaving it out would hide
 * those headings from the picker for exactly the window in which they are most likely to
 * be referenced. Two-stage membership is CZNA-581/v3.1 §2: subject, genre form,
 * chronological, conspect group — geographic (this hook's only PROPOSAL_INCLUSIVE_TYPES
 * member) moved off this list, so the branch below is currently unreachable in production
 * but stays correct for whichever type PROPOSAL_INCLUSIVE_TYPES grows to next.
 *
 * <p>Added only for types that can reach that state, because the endpoint takes a singular
 * status and each entry costs one more request per keystroke-debounced search. For every
 * single-stage type the extra query could only ever return nothing.
 */
var pickerProposalStatuses = function (recordType) { return (isTwoStageApproval(recordType)
    ? __spreadArray(__spreadArray([], PICKER_PROPOSAL_STATUSES, true), [ProposalStatus.PENDING_NATIONAL_REVIEW], false) : PICKER_PROPOSAL_STATUSES); };
var useRecordPickerOptions = function (_a) {
    var recordType = _a.recordType, _b = _a.preload, preload = _b === void 0 ? false : _b, alsoRecordTypes = _a.alsoRecordTypes;
    var ky = useOkapiKy();
    var intl = useIntl();
    var _c = useState(''), filterText = _c[0], setFilterText = _c[1];
    var debounceRef = useRef(undefined);
    var includeProposals = PROPOSAL_INCLUSIVE_TYPES.has(recordType);
    var handleFilter = useCallback(function (text) {
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(function () {
            setFilterText(text);
        }, DEBOUNCE_MS);
    }, []);
    useEffect(function () {
        return function () { return clearTimeout(debounceRef.current); };
    }, []);
    var anchor = typedAnchor(filterText);
    // The index endpoint takes the registers comma-separated in pickable mode.
    var registerTypes = __spreadArray([recordType], (alsoRecordTypes !== null && alsoRecordTypes !== void 0 ? alsoRecordTypes : []), true).join(',');
    // No keepPreviousData: the previous window was opened at other text, and offering its
    // rows under the new text is the stale-list effect NK saw between keystrokes.
    var _d = useQuery(['czna-record-picker-register', registerTypes, anchor !== null && anchor !== void 0 ? anchor : ''], function () { return fetchRecordIndex(ky, {
        recordType: registerTypes,
        anchor: anchor,
        after: anchor ? PICKER_WINDOW : PICKER_OPEN_WINDOW,
        pickable: true,
    }); }, {
        enabled: !!recordType && (preload || !!anchor),
        staleTime: 30 * 1000,
    }), registerData = _d.data, registerLoading = _d.isLoading, registerFetching = _d.isFetching, registerFailed = _d.isError;
    // CZNA-197: proposals branch. The proposals endpoint requires a singular,
    // mandatory status, so the included statuses are fetched in parallel and
    // merged — two for a single-stage type, three for a two-stage one (CZNA-552). Short staleTime (5s) so a proposal created via the in-tab modal in
    // the same session shows up promptly on a re-search.
    var _e = useQuery(['czna-record-picker-proposals', recordType, filterText], function () { return __awaiter(void 0, void 0, void 0, function () {
        var responses;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0: return [4 /*yield*/, Promise.all(pickerProposalStatuses(recordType).map(function (status) {
                        return fetchProposals(ky, {
                            recordType: recordType,
                            heading: filterText || undefined,
                            status: status,
                            // CZNA-197 (SF-2): only CREATE_NEW proposals are publishable as a new
                            // record. The backend re-link (ProposalServiceImpl.publishAsRecord)
                            // rewrites references only for CREATE_NEW proposals; surfacing an
                            // UPDATE_EXISTING proposal here would let a cataloger pick a
                            // proposal.id that never becomes a record id → dangling reference.
                            proposalType: ProposalType.CREATE_NEW,
                            sortBy: 'headingValue',
                            sortOrder: 'asc',
                            limit: PROPOSAL_LIMIT,
                        });
                    }))];
                case 1:
                    responses = _a.sent();
                    return [2 /*return*/, responses.flatMap(function (r) { return r.items || []; })];
            }
        });
    }); }, {
        enabled: includeProposals && !!recordType && filterText.length > 0,
        staleTime: 5 * 1000,
    }), proposalsData = _e.data, proposalsFetching = _e.isFetching;
    var dataOptions = useMemo(function () {
        var recordOptions = firstRowPerRecord((registerData === null || registerData === void 0 ? void 0 : registerData.entries) || [])
            .map(function (entry) { return ({
            value: entry.recordId,
            label: registerRowLabel(entry, intl),
            // The PREFERRED heading — what the subfield stores. A variant's text must not
            // leak into form state.
            heading: insertedHeading(entry),
            found: entry.headingValue,
        }); });
        if (!includeProposals || !proposalsData || proposalsData.length === 0) {
            return recordOptions;
        }
        // CZNA-197: merge pending proposals. Dedup by heading (case-insensitive):
        // an approved record with the same heading wins, so a proposal that has
        // since been published does not appear twice. Also dedup proposals against
        // each other (a heading could match in both PENDING_REVIEW and
        // CHANGES_REQUESTED, or be returned twice across the two queries).
        var approvedHeadings = new Set(recordOptions.map(function (o) { return o.heading.toLowerCase(); }));
        var seenProposalHeadings = new Set();
        var proposalLabel = function (heading) {
            return intl.formatMessage({ id: 'ui-czna-authorities.recordPicker.proposalSuffix' }, { heading: heading });
        };
        var proposalOptions = [];
        proposalsData.forEach(function (p) {
            var key = (p.headingValue || '').toLowerCase();
            // Skip blank headings, proposals already approved as a record (record
            // wins), and duplicate headings across the two status queries.
            if (!key || approvedHeadings.has(key) || seenProposalHeadings.has(key)) {
                return;
            }
            seenProposalHeadings.add(key);
            proposalOptions.push({
                value: p.id,
                label: proposalLabel(p.headingValue),
                heading: p.headingValue,
                // A proposal is not yet a register term and carries no 901$c; the subfield
                // stores its heading. No code-bearing register is proposal-inclusive today
                // (PROPOSAL_INCLUSIVE_TYPES = GEOGRAPHIC), so this branch never meets one.
                code: null,
                isProposal: true,
            });
        });
        return __spreadArray(__spreadArray([], recordOptions, true), proposalOptions, true);
    }, [registerData, proposalsData, includeProposals, intl]);
    return {
        dataOptions: dataOptions,
        loading: registerLoading,
        fetching: registerFetching || proposalsFetching,
        failed: registerFailed,
        // No keepPreviousData, so data present is data for the current key.
        windowAnchor: registerData ? (anchor !== null && anchor !== void 0 ? anchor : '') : null,
        handleFilter: handleFilter,
        // The register is opened at the typed text on the server; Selection must not filter
        // the window again by its own rule.
        asyncFilter: true,
    };
};
export default useRecordPickerOptions;
